import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretDown, UserCircle, IdentificationCard, GearSix, Crown, UserPlus, SignOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useInvite } from "@/context/InviteContext";

// Avatar + name + chevron in the app header. Opens a small account menu.
// Sign out sits alone at the bottom, behind a separator, so it can't be hit
// by accident on the way to Profile or Settings.

const ITEM_CLS =
  "flex cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2 text-[14px] text-[#E9E3D3] outline-none transition-colors " +
  "data-[highlighted]:bg-white/[0.06] data-[highlighted]:text-[#F6F1E4]";

export function ProfileMenu() {
  const { user, logout } = useAuth();
  const { openInvite } = useInvite();
  const navigate = useNavigate();
  const initial = user?.name?.[0]?.toUpperCase() || "?";
  // Falls back to the initial letter if the picture URL 404s/fails to
  // load, instead of leaving the browser's broken-image icon showing.
  const [imgError, setImgError] = useState(false);
  useEffect(() => setImgError(false), [user?.picture]);

  return (
    <Dropdown.Root modal={false}>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          data-testid="ia-nav-profile"
          aria-label={`Account menu for ${user?.name || "your account"}`}
          className="group flex shrink-0 items-center gap-2.5 rounded-full py-1 pl-1 pr-1 outline-none transition-colors duration-200 hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#07090d] data-[state=open]:bg-white/[0.06] xl:pr-3"
        >
          <span className="block h-8 w-8 shrink-0 rounded-full p-[1.5px] xl:h-9 xl:w-9"
            style={{ background: "linear-gradient(145deg, #F3D98A 0%, #D4AF37 45%, #8A6A1C 100%)" }}>
            {user?.picture && !imgError ? (
              <img src={user.picture} alt="" onError={() => setImgError(true)} className="h-full w-full rounded-full object-cover ring-1 ring-black/60" />
            ) : (
              <span className="flex h-full w-full items-center justify-center rounded-full bg-[#14110a] font-display text-[13px] text-gold-soft">{initial}</span>
            )}
          </span>
          <span className="hidden max-w-[150px] truncate text-[14px] font-medium text-[#F3EEE2] xl:block">{user?.name}</span>
          <CaretDown size={12} weight="bold" aria-hidden="true"
            className="hidden text-gold-soft transition-transform duration-200 group-data-[state=open]:rotate-180 motion-reduce:transition-none xl:block" />
        </button>
      </Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={10}
          data-testid="ia-nav-profile-menu"
          className="z-50 w-64 rounded-2xl border border-gold/20 bg-[#0B0D12]/[0.98] p-1.5 shadow-[0_18px_50px_rgba(0,0,0,0.65)] backdrop-blur-xl"
        >
          <div className="px-3 pb-2 pt-2.5">
            <p className="truncate text-[14px] font-semibold text-[#F6F1E4]">{user?.name}</p>
            {user?.email && <p className="truncate text-[12px] text-dim">{user.email}</p>}
          </div>
          <Dropdown.Separator className="my-1 h-px bg-white/[0.07]" />

          <Dropdown.Item className={ITEM_CLS} onSelect={() => navigate("/app/you")} data-testid="profile-menu-you">
            <UserCircle size={18} weight="light" className="text-gold-soft" aria-hidden="true" />You
          </Dropdown.Item>
          <Dropdown.Item className={ITEM_CLS} onSelect={() => navigate("/app/profile")} data-testid="profile-menu-profile">
            <IdentificationCard size={18} weight="light" className="text-gold-soft" aria-hidden="true" />Profile
          </Dropdown.Item>
          <Dropdown.Item className={ITEM_CLS} onSelect={() => navigate("/app/settings")} data-testid="profile-menu-settings">
            <GearSix size={18} weight="light" className="text-gold-soft" aria-hidden="true" />Settings
          </Dropdown.Item>
          <Dropdown.Item className={ITEM_CLS} onSelect={() => navigate("/app/upgrade")} data-testid="profile-menu-premiere">
            <Crown size={18} weight="light" className="text-gold-soft" aria-hidden="true" />
            {user?.is_premium ? "Premiere membership" : "Upgrade to Premiere"}
          </Dropdown.Item>
          <Dropdown.Item className={ITEM_CLS} onSelect={() => openInvite()} data-testid="profile-menu-invite">
            <UserPlus size={18} weight="light" className="text-gold-soft" aria-hidden="true" />Invite friends
          </Dropdown.Item>

          <Dropdown.Separator className="my-1 h-px bg-white/[0.07]" />
          <Dropdown.Item className={ITEM_CLS} onSelect={() => logout()} data-testid="profile-menu-signout">
            <SignOut size={18} weight="light" className="text-dim" aria-hidden="true" />Sign out
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
